import type { MouseEvent } from "react";
import { resolveYearEndStrip } from "./yearEndStripSchedule";

/**
 * Document-relative layout top, ignoring CSS transforms — the form fades in
 * with a translateY, so getBoundingClientRect() would overshoot mid-reveal.
 */
function pageTop(el: HTMLElement): number {
  let top = 0;
  for (
    let n: HTMLElement | null = el;
    n;
    n = n.offsetParent as HTMLElement | null
  ) {
    top += n.offsetTop;
  }
  return top;
}

/**
 * Smooth-scroll to the lead form and put the cursor in its first field, so
 * the strip lands visitors ready to type rather than just near the form.
 */
function goToForm(e: MouseEvent<HTMLAnchorElement>, formId: string) {
  // Leave modified / non-primary clicks (new tab, new window) to the browser.
  if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
    return;
  }
  const section = document.getElementById(formId);
  if (!section) return; // fall back to the plain hash jump
  e.preventDefault();

  const field = section.querySelector<HTMLElement>(
    'form input:not([type="hidden"]):not([tabindex="-1"]), form select, form textarea',
  );
  const headerH =
    e.currentTarget.closest("header")?.getBoundingClientRect().height ?? 0;
  // Land on the section's top edge (headline + form). On single-column
  // layouts the intro can push the field below the fold, so then land on
  // the field itself instead.
  let top = pageTop(section) - headerH;
  if (field) {
    const fieldTop = pageTop(field);
    if (fieldTop + field.offsetHeight > top + window.innerHeight - 16) {
      top = fieldTop - headerH - 24;
    }
  }
  const reduceMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  window.scrollTo({ top, behavior: reduceMotion ? "auto" : "smooth" });
  history.replaceState(null, "", `#${formId}`);
  // preventScroll so focusing doesn't cut the smooth scroll short.
  field?.focus({ preventScroll: true });
}

interface YearEndStripProps {
  /** `id` of the page's lead-form section the link scrolls to. */
  formId: string;
}

/**
 * Seasonal announcement strip above the landing and use-case navs. Whether
 * it shows, and its wording, follow the calendar in `yearEndStripSchedule.ts`.
 * Only the call to action is a link; the rest is plain text.
 */
export const YearEndStrip = ({ formId }: YearEndStripProps) => {
  const message = resolveYearEndStrip(
    new Date(),
    window.location.search,
    import.meta.env.DEV,
  );
  if (!message) return null;
  return (
    <p className="bg-fwd-ink text-white/75 text-center text-xs sm:text-sm leading-snug px-4 py-2">
      {message.lead}{" "}
      <a
        href={`#${formId}`}
        onClick={(e) => goToForm(e, formId)}
        className="font-semibold text-fwd-yellow underline decoration-fwd-yellow/40 underline-offset-2 hover:decoration-fwd-yellow transition-colors"
      >
        {message.link}
      </a>
      {message.tail}
    </p>
  );
};
